import { InstanceBatch } from "./instance_batch.js";
import { ShaderBuilder } from "../../shader_builder/shader_builder.js";
import { AttributeSize, BlendMode, ColorMode } from "../types/webgl_types.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { WebGLStaticAtlasBuilder } from "../rendering/textures/webgl_static_atlas_bulder.js";
import { Vfx2DGlobalAtlas } from "../rendering/textures/vfx_2d_global_atlas.js";

export const GenericSpriteLayout = {
  a_position: AttributeSize.Vec2,
  a_size: AttributeSize.Vec2,
  a_min_uv: AttributeSize.Vec2,
  a_max_uv: AttributeSize.Vec2,
  a_color: AttributeSize.Vec3,
  a_alpha: AttributeSize.Float,
  a_angle: AttributeSize.Float,
  a_additive: AttributeSize.Float,
  a_progress: AttributeSize.Float,
  a_color_mode: AttributeSize.Float,
} as const;

export class GenericSpriteBatch extends InstanceBatch<typeof GenericSpriteLayout>
{
  private default_color_mode: ColorMode;

  constructor( config?: { max_instances?: number; blend_mode?: BlendMode; default_color_mode?: ColorMode } )
  {
    super( {
      max_instances: config?.max_instances ?? 100,
      vertices_per_instance: 6,
      primitive_type: WebGL2RenderingContext.TRIANGLES,
      blend_mode: config?.blend_mode ?? BlendMode.Normal,
      layout: GenericSpriteLayout,
    } );

    this.default_color_mode = config?.default_color_mode ?? ColorMode.Tinted;

    this.buildShader(
      new ShaderBuilder()
        .includeChunk( 'remap' )
        .withUniform( 'sampler2D', 'u_texture' )
        .withVarying( 'vec2', 'v_uv' )
        .withVarying( 'vec3', 'v_color' )
        .withVarying( 'float', 'v_alpha' )
        .withVarying( 'float', 'v_additive' )
        .withVarying( 'float', 'v_progress' )
        .withVarying( 'float', 'v_color_mode' )
        .withVertexLogic( `
          angle = a_angle;
          v_uv = mix(a_min_uv, a_max_uv, uv);
          v_color = a_color;
          v_alpha = a_alpha;
          v_additive = a_additive;
          v_progress = a_progress;
          v_color_mode = a_color_mode;
        ` )
        .withFragmentLogic( `
          vec4 tex = texture(u_texture, v_uv);
          float alpha = tex.a * v_alpha;
          float out_alpha = alpha * (1.0 - v_additive);
          vec3 base_rgb = (v_color_mode > 1.5) ? v_color : ((v_color_mode > 0.5) ? tex.rgb : tex.rgb * v_color);
          fragColor = vec4(base_rgb * alpha, out_alpha);
        ` )
    );
  }

  protected override onInit( gl: WebGL2RenderingContext ): void
  {
    const atlas_data = Vfx2DGlobalAtlas.getAtlasData();
    const atlas = WebGLStaticAtlasBuilder.createAtlasFromData( gl, atlas_data );
    this.textures = [ { location: 'u_texture', texture: atlas.texture, unit: 0 } ];
  }

  protected override updateInstance( gpu_data: Float32Array, offset: number, progress: number, now: number, idx: number ): boolean
  {
    gpu_data[offset + this.attr_offset.a_progress] = progress;
    return true;
  }

  /**
   * Створює новий інстанс спрайту в батчі з використанням глобального атласу.
   */
  public spawnSprite(
    img_id: ImgID,
    pos: Vector2,
    size: Vector2,
    color: { x: number; y: number; z: number },
    alpha: number,
    angle: number,
    additive: number,
    start_time: number,
    duration: number,
    color_mode: ColorMode = this.default_color_mode
  ): number
  {
    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 ) return -1;

    const atlas_data = Vfx2DGlobalAtlas.getAtlasData();
    const bounds = atlas_data.getUVRect( img_id );

    const data = this.buffer.getDataArray();
    const offset = idx * this.buffer.floats_per_instance;

    data[offset + this.attr_offset.a_position] = pos.x;
    data[offset + this.attr_offset.a_position + 1] = pos.y;
    data[offset + this.attr_offset.a_size] = size.x;
    data[offset + this.attr_offset.a_size + 1] = size.y;
    data[offset + this.attr_offset.a_min_uv] = bounds.min_x;
    data[offset + this.attr_offset.a_min_uv + 1] = bounds.min_y;
    data[offset + this.attr_offset.a_max_uv] = bounds.max_x;
    data[offset + this.attr_offset.a_max_uv + 1] = bounds.max_y;
    data[offset + this.attr_offset.a_color] = color.x;
    data[offset + this.attr_offset.a_color + 1] = color.y;
    data[offset + this.attr_offset.a_color + 2] = color.z;
    data[offset + this.attr_offset.a_alpha] = alpha;
    data[offset + this.attr_offset.a_angle] = angle;
    data[offset + this.attr_offset.a_additive] = additive;
    data[offset + this.attr_offset.a_progress] = 0.0;
    data[offset + this.attr_offset.a_color_mode] = color_mode;

    return this.getStableId( idx );
  }

  public spawnSpriteUV(
    uv_rect: { min_x: number; min_y: number; max_x: number; max_y: number },
    pos: Vector2,
    size: Vector2,
    color: { x: number; y: number; z: number },
    alpha: number,
    angle: number,
    additive: number,
    start_time: number,
    duration: number,
    color_mode: ColorMode = this.default_color_mode
  ): number
  {
    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 ) return -1;

    const data = this.buffer.getDataArray();
    const offset = idx * this.buffer.floats_per_instance;

    data[offset + this.attr_offset.a_position] = pos.x;
    data[offset + this.attr_offset.a_position + 1] = pos.y;
    data[offset + this.attr_offset.a_size] = size.x;
    data[offset + this.attr_offset.a_size + 1] = size.y;
    data[offset + this.attr_offset.a_min_uv] = uv_rect.min_x;
    data[offset + this.attr_offset.a_min_uv + 1] = uv_rect.min_y;
    data[offset + this.attr_offset.a_max_uv] = uv_rect.max_x;
    data[offset + this.attr_offset.a_max_uv + 1] = uv_rect.max_y;
    data[offset + this.attr_offset.a_color] = color.x;
    data[offset + this.attr_offset.a_color + 1] = color.y;
    data[offset + this.attr_offset.a_color + 2] = color.z;
    data[offset + this.attr_offset.a_alpha] = alpha;
    data[offset + this.attr_offset.a_angle] = angle;
    data[offset + this.attr_offset.a_additive] = additive;
    data[offset + this.attr_offset.a_progress] = 0.0;
    data[offset + this.attr_offset.a_color_mode] = color_mode;

    return this.getStableId( idx );
  }

  /**
   * Дозволяє оновити параметри конкретного активного інстансу з CPU (наприклад, колір, альфу, кут).
   */
  public updateSpriteData(
    stable_id: number,
    pos: Vector2,
    size: Vector2,
    alpha: number,
    angle: number,
    color?: { x: number; y: number; z: number },
    uv_rect?: { min_x: number; min_y: number; max_x: number; max_y: number },
    color_mode?: ColorMode
  ): void
  {
    const idx = this.stable_to_idx.get( stable_id );
    if ( idx === undefined || idx === -1 ) return;

    const data = this.buffer.getDataArray();
    const offset = idx * this.buffer.floats_per_instance;

    data[offset + this.attr_offset.a_position] = pos.x;
    data[offset + this.attr_offset.a_position + 1] = pos.y;
    data[offset + this.attr_offset.a_size] = size.x;
    data[offset + this.attr_offset.a_size + 1] = size.y;
    data[offset + this.attr_offset.a_alpha] = alpha;
    data[offset + this.attr_offset.a_angle] = angle;

    if ( color )
    {
      data[offset + this.attr_offset.a_color] = color.x;
      data[offset + this.attr_offset.a_color + 1] = color.y;
      data[offset + this.attr_offset.a_color + 2] = color.z;
    }
    if ( uv_rect )
    {
      data[offset + this.attr_offset.a_min_uv] = uv_rect.min_x;
      data[offset + this.attr_offset.a_min_uv + 1] = uv_rect.min_y;
      data[offset + this.attr_offset.a_max_uv] = uv_rect.max_x;
      data[offset + this.attr_offset.a_max_uv + 1] = uv_rect.max_y;
    }
    if ( color_mode !== undefined )
    {
      data[offset + this.attr_offset.a_color_mode] = color_mode;
    }
  }
}
